/**
 * Keys that are known to resolve to objects (parent nodes with _self) in
 * unflattened messages. For these we request the ._self key first to avoid
 * INSUFFICIENT_PATH.
 */
const PARENT_KEYS = new Set([
  'nav.utility.account',
  'nav.utility.cart',
  'nav.utility.help',
  'nav.utility.menu',
]);

export type TranslationValues = Record<
  string,
  string | number | Date | undefined
>;

type TranslateFunction = (key: string, values?: TranslationValues) => string;

/**
 * Wraps a next-intl `t` function to use the `._self` value when the key
 * resolves to an object (INSUFFICIENT_PATH).
 *
 * For known parent keys we request key._self first. For others we try t(key)
 * and on error retry with t(key + '._self').
 */
export function translateWithSelfFallback(
  t: TranslateFunction,
  key: string,
  values?: TranslationValues,
  hasSelfValue?: (selfKey: string) => boolean
): string {
  const selfKey = key + '._self';
  const shouldTrySelfFirst = hasSelfValue
    ? hasSelfValue(selfKey)
    : PARENT_KEYS.has(key);

  if (shouldTrySelfFirst) {
    try {
      return t(selfKey, values);
    } catch {
      return t(key, values);
    }
  }
  try {
    return t(key, values);
  } catch {
    try {
      return t(selfKey, values);
    } catch (err) {
      throw err;
    }
  }
}
